/**
 * Store reset registry.
 *
 * Bug-hunt #84: `useAuthStore.logout()` historically only cleared its own
 * state. `useStaffStore.orgOverride` and `useFailureStore.failure` (and any
 * future store) survived a logout — the next user logging in on the same tab
 * could inherit the previous user's staff impersonation or failure screen.
 *
 * Every Zustand store in this app registers its reset function here. The
 * `resetAllStores()` helper is invoked from the API client's auth-failure
 * hook (see `main.tsx`), and tests can call it between cases for hygiene.
 *
 * Usage:
 *
 *   export const useFooStore = create<...>()((set) => ({...}));
 *   registerStoreReset(() => useFooStore.setState(INITIAL_FOO_STATE, true), { onUserSwitch: true });
 *
 * `onUserSwitch` (#1336) says whether the reset also runs when the SAME token
 * starts answering as a different user id (a direct merge of the person's
 * second login, `adoptSurvivingUser` below). It defaults to true. A store the
 * person would see change under their hands (the auth store itself, an open
 * panel, a banner, the ctx gate phase) passes false: a sign-out resets it, a
 * switch leaves it alone. `storeResetRegistered.guard.test.ts` makes every
 * store state its choice.
 */

type StoreReset = () => void;

export interface StoreResetOptions {
  /** Also run this reset when the session's user id changes under the same token. Default true. */
  onUserSwitch?: boolean;
}

const registry = new Map<StoreReset, Required<StoreResetOptions>>();

export function registerStoreReset(reset: StoreReset, options: StoreResetOptions = {}): () => void {
  registry.set(reset, { onUserSwitch: options.onUserSwitch ?? true });
  return () => registry.delete(reset);
}

/**
 * The resets that also run when the session's user id changes under the same
 * token (#1336; `adoptSurvivingUser` in `./userSwitch`). Lives here because the
 * registry is private to this module.
 */
export function runUserSwitchResets(): void {
  for (const [reset, options] of registry) {
    if (!options.onUserSwitch) continue;
    try {
      reset();
    } catch (err) {
      console.error('[store-registry] switch reset threw', err);
    }
  }
}

export function resetAllStores(): void {
  for (const reset of registry.keys()) {
    try {
      reset();
    } catch (err) {
      console.error('[store-registry] reset threw', err);
    }
  }
}

/**
 * Session-scoped cleanups that live OUTSIDE Zustand (e.g. the TanStack
 * Query cache, which is created in main.tsx and can't be imported here).
 *
 * Audit C7: voluntary logout used to call only `useAuthStore.logout()`,
 * leaving the previous user's query cache and persisted staff org override
 * intact for the next account on the same tab. Every logout path — the
 * 401 auth-failure hook, the Logout route, and the cross-tab storage
 * listener — must go through `clearSession()` so they can't drift apart.
 */
const sessionCleanups = new Set<StoreReset>();

export function registerSessionCleanup(cleanup: StoreReset): () => void {
  sessionCleanups.add(cleanup);
  return () => sessionCleanups.delete(cleanup);
}

export function clearSession(): void {
  for (const cleanup of sessionCleanups) {
    try {
      cleanup();
    } catch (err) {
      console.error('[store-registry] session cleanup threw', err);
    }
  }
  resetAllStores();
}

/** Test-only: empty the registry. Production code must never call this. */
export function _clearStoreRegistry(): void {
  registry.clear();
}

/** Test-only: snapshot of registered resets (count). */
export function _registeredStoreCount(): number {
  return registry.size;
}
